import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from '../hooks/useTranslation';
import { useCss } from '../context/CssContext';

const RulesPopup = ({ isOpen, onClose, bonusTitle, rules = [], bonusImage, onRequest }) => {
  const { t } = useTranslation();
  const { cardLayoutMode } = useCss();
  const isTextOnly = cardLayoutMode === 'text-only';
  const [imgErr, setImgErr] = useState(false);

  useEffect(() => {
    const root = document.getElementById('root');
    if (!root) return;
    if (isOpen) root.style.overflow = 'hidden';
    return () => { root.style.overflow = ''; };
  }, [isOpen]);

  useEffect(() => { setImgErr(false); }, [bonusImage]);

  if (!isOpen) return null;

  return createPortal(
    <div style={S.overlay} className="modal-overlay-anim">
      <div style={S.popup} className="modal-popup-anim">

        {/* ── X Butonu ── */}
        <button style={S.xBtn} onClick={onClose}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="M18 6 6 18M6 6l12 12"/>
          </svg>
        </button>

        {/* ── Header: Bonus image + Badge + Title ── */}
        {isTextOnly ? (
          /* GÖRSELSİZ: Ortalı, ikon-rozet üstte, başlık altında — temiz tipografi */
          <div style={S.headerTextOnly}>
            <div style={S.iconRing}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="5" y="2" width="14" height="20" rx="2"/>
                <path d="M9 7h6M9 11h6M9 15h4"/>
              </svg>
            </div>
            <div style={S.badgeCentered}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                <path d="M12 2l3 7h7l-5.5 4 2 7L12 16l-6.5 4 2-7L2 9h7z"/>
              </svg>
              {t('popup.rulesTitle', 'BONUS KURALLARI')}
            </div>
            <div style={S.bonusTitleCentered}>{bonusTitle}</div>
          </div>
        ) : (
          /* VARSAYILAN: Görselli — sol resim + sağ başlık */
          <div style={S.header}>
            {bonusImage && !imgErr ? (
              <div style={S.imgCard}>
                <img
                  src={bonusImage}
                  alt={bonusTitle}
                  style={S.imgCardImg}
                  onError={() => setImgErr(true)}
                />
                <div style={S.imgCardGlow}/>
              </div>
            ) : (
              <div style={S.imgCardFallback}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="rgba(56,189,248,0.6)" strokeWidth="1.5">
                  <rect x="3" y="3" width="18" height="18" rx="3"/>
                  <path d="M8 8h8M8 12h5"/>
                </svg>
              </div>
            )}

            <div style={S.headerInfo}>
              <div style={S.badge}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                  <path d="M12 2l3 7h7l-5.5 4 2 7L12 16l-6.5 4 2-7L2 9h7z"/>
                </svg>
                {t('popup.rulesTitle', 'BONUS KURALLARI')}
              </div>
              <div style={S.bonusTitle}>{bonusTitle}</div>
            </div>
          </div>
        )}

        {/* ── Separator ── */}
        <div style={S.sep}/>

        {/* ── Kurallar ── */}
        <div style={S.scroll} className="rp-scroll">
          {rules && rules.length > 0 ? (
            <div style={S.list}>
              {rules.map((rule, i) => (
                <div key={i} style={S.card} className="rp-card">
                  <div style={S.cardBar}/>
                  <div style={S.cardDot}/>
                  <div style={S.cardBody}>
                    {rule.title && <div style={S.ruleTitle}>{rule.title}</div>}
                    <div style={S.ruleText}>
                      {rule.metin.split('\n').map((line, j, arr) => (
                        <React.Fragment key={j}>
                          {line}{j < arr.length - 1 && <br/>}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={S.empty}>{t('popup.noRules', 'Bu bonus için henüz kural belirtilmemiştir.')}</div>
          )}
        </div>

        {/* ── Footer ── */}
        <div style={S.footer}>
          <button style={S.anladimBtn} onClick={onClose}>
            {t('buttons.understood', 'ANLADIM')}
          </button>
          {onRequest && (
            <button style={S.talepBtn} onClick={onRequest}>
              {t('buttons.request', 'TALEP ET')} →
            </button>
          )}
        </div>

      </div>
      <style>{`
        .rp-scroll::-webkit-scrollbar{width:4px}
        .rp-scroll::-webkit-scrollbar-track{background:transparent}
        .rp-scroll::-webkit-scrollbar-thumb{background:rgba(255,255,255,0.12);border-radius:4px}
        .rp-scroll::-webkit-scrollbar-thumb:hover{background:rgba(var(--primary-color-rgb),0.5)}
        .rp-card:hover{background:rgba(255,255,255,0.07)!important}
      `}</style>
    </div>,
    document.body
  );
};

const S = {
  overlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(2,5,14,0.55)',
    backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
    display: 'flex', alignItems: 'safe center', justifyContent: 'center',
    overflowY: 'auto',
    zIndex: 9999, padding: '16px', boxSizing: 'border-box',
  },
  popup: {
    position: 'relative',
    background: 'var(--rules-popup-bg-color, rgba(14,165,233,0.08))',
    backdropFilter: 'blur(40px)', WebkitBackdropFilter: 'blur(40px)',
    border: '1px solid var(--rules-popup-border-color, rgba(14,165,233,0.20))',
    borderRadius: '18px',
    boxShadow: '0 24px 64px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.10)',
    width: '100%', maxWidth: '500px',
    maxHeight: '88svh',
    display: 'flex', flexDirection: 'column',
    overflow: 'hidden',
    fontFamily: "'Inter',sans-serif",
  },
  xBtn: {
    position: 'absolute', top: '14px', right: '14px',
    width: '30px', height: '30px', borderRadius: '50%',
    background: 'rgba(255,255,255,0.07)',
    border: '1px solid rgba(255,255,255,0.12)',
    color: 'rgba(255,255,255,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', padding: 0, zIndex: 2,
  },
  header: {
    display: 'flex', alignItems: 'center', gap: '16px',
    padding: '20px 20px 16px',
    flexShrink: 0,
  },
  /* GÖRSELSİZ varyant — ortalı header */
  headerTextOnly: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px',
    padding: '28px 24px 18px',
    flexShrink: 0,
    textAlign: 'center',
  },
  /* Glow icon ring — görsel yerine geçen vurgu elemanı */
  iconRing: {
    width: '56px', height: '56px',
    borderRadius: '50%',
    background: 'radial-gradient(circle at 30% 30%, rgba(var(--primary-color-rgb),0.32), rgba(var(--primary-color-rgb),0.08) 70%)',
    border: '1px solid rgba(var(--primary-color-rgb),0.35)',
    color: 'var(--primary-color, #38bdf8)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 0 28px rgba(var(--primary-color-rgb),0.30), inset 0 1px 0 rgba(255,255,255,0.12)',
  },
  badgeCentered: {
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    background: 'var(--rules-popup-badge-bg-color, rgba(14,165,233,0.15))',
    border: '1px solid var(--rules-popup-badge-border-color, rgba(14,165,233,0.28))',
    borderRadius: '20px',
    padding: '4px 14px',
    color: 'var(--rules-popup-badge-text-color, var(--primary-color))',
    fontSize: '10.5px', fontWeight: '700', letterSpacing: '0.10em',
  },
  bonusTitleCentered: {
    color: 'var(--rules-popup-title-color, #fff)',
    fontSize: '20px', fontWeight: '700',
    fontFamily: "'Outfit','Inter',sans-serif",
    lineHeight: 1.25,
    textTransform: 'uppercase',
    letterSpacing: '0.02em',
    maxWidth: '100%',
  },
  /* Bonus görsel glass card — bonus kartı oranında kare (1:1) */
  imgCard: {
    position: 'relative',
    width: '100px', height: '100px', minWidth: '100px', aspectRatio: '1 / 1',
    borderRadius: '12px',
    overflow: 'hidden',
    border: '1px solid rgba(255,255,255,0.12)',
    boxShadow: '0 8px 24px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.12)',
    flexShrink: 0,
  },
  imgCardImg: {
    width: '100%', height: '100%', objectFit: 'cover',
    objectPosition: 'center', display: 'block',
  },
  imgCardGlow: {
    position: 'absolute', inset: 0,
    background: 'linear-gradient(135deg, rgba(var(--primary-color-rgb),0.08) 0%, transparent 60%)',
    pointerEvents: 'none',
  },
  imgCardFallback: {
    width: '100px', height: '100px', minWidth: '100px', aspectRatio: '1 / 1',
    borderRadius: '12px',
    background: 'rgba(var(--primary-color-rgb),0.08)',
    border: '1px solid rgba(var(--primary-color-rgb),0.18)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  headerInfo: {
    display: 'flex', flexDirection: 'column', gap: '8px', flex: 1, minWidth: 0,
  },
  badge: {
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    background: 'var(--rules-popup-badge-bg-color, rgba(14,165,233,0.15))',
    border: '1px solid var(--rules-popup-badge-border-color, rgba(14,165,233,0.28))',
    borderRadius: '20px',
    padding: '4px 12px',
    color: 'var(--rules-popup-badge-text-color, var(--primary-color))',
    fontSize: '10.5px', fontWeight: '700', letterSpacing: '0.08em',
    alignSelf: 'flex-start',
  },
  bonusTitle: {
    color: 'var(--rules-popup-title-color, #fff)',
    fontSize: '18px', fontWeight: '700',
    fontFamily: "'Outfit','Inter',sans-serif",
    lineHeight: 1.3,
  },
  sep: {
    height: '1px',
    background: 'rgba(255,255,255,0.07)',
    flexShrink: 0, marginBottom: '4px',
  },
  scroll: {
    flex: 1, minHeight: 0,
    overflowY: 'auto', overflowX: 'hidden',
    padding: '12px 20px',
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(255,255,255,0.12) transparent',
  },
  list: { display: 'flex', flexDirection: 'column', gap: '8px' },
  card: {
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '13px 16px',
    background: 'var(--rules-popup-rule-bg-color, rgba(14,165,233,0.07))',
    border: '1px solid var(--rules-popup-rule-border-color, rgba(14,165,233,0.15))',
    borderRadius: '10px',
    borderLeft: '3px solid var(--rules-popup-rule-accent-color, var(--primary-color))',
    transition: 'background 0.18s ease',
  },
  cardBar: { display: 'none' },
  cardDot: {
    width: '6px', height: '6px', borderRadius: '50%',
    background: 'var(--rules-popup-rule-accent-color, var(--primary-color))',
    boxShadow: '0 0 6px rgba(var(--primary-color-rgb),0.7)',
    flexShrink: 0,
  },
  cardBody: { flex: 1, minWidth: 0 },
  ruleTitle: {
    color: 'var(--rules-popup-rule-title-color, #fff)',
    fontSize: '13.5px', fontWeight: '600',
    marginBottom: '3px', lineHeight: 1.3,
  },
  ruleText: {
    color: 'var(--rules-popup-rule-text-color, rgba(255,255,255,0.60))',
    fontSize: '13px',
    fontWeight: '400', lineHeight: 1.55,
  },
  empty: {
    color: 'rgba(255,255,255,0.35)', fontSize: '13px',
    textAlign: 'center', padding: '40px 0',
  },
  footer: {
    display: 'flex', gap: '10px', justifyContent: 'flex-end',
    padding: '14px 20px 18px',
    borderTop: '1px solid rgba(255,255,255,0.07)',
    flexShrink: 0,
  },
  anladimBtn: {
    padding: '10px 24px', borderRadius: '10px',
    border: '1px solid rgba(255,255,255,0.15)',
    background: 'var(--rules-popup-understood-bg-color, rgba(255,255,255,0.07))',
    color: 'var(--rules-popup-understood-text-color, rgba(255,255,255,0.75))',
    fontSize: '12.5px', fontWeight: '700', letterSpacing: '0.06em',
    cursor: 'pointer', fontFamily: "'Inter',sans-serif",
  },
  talepBtn: {
    padding: '10px 24px', borderRadius: '10px',
    border: '1px solid rgba(255,255,255,0.18)',
    background: 'var(--rules-popup-request-bg-color, rgba(255,255,255,0.09))',
    color: 'var(--rules-popup-request-text-color, #ffffff)',
    fontSize: '12.5px', fontWeight: '700', letterSpacing: '0.06em',
    cursor: 'pointer', fontFamily: "'Inter',sans-serif",
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.10)',
  },
};

export default RulesPopup;
